import React, {
  ComponentType,
  FunctionComponent,
  useEffect,
  useState,
} from 'react';
import { useNotify, useRefresh, useTranslate } from 'react-admin';
import { useForm } from 'react-final-form';
import { get } from 'lodash';
import { httpClient } from '../../providers/dataProvider';
import useConfig from '../../hooks/useConfig';
import { getCurrentUserId } from '../../helpers/auth';
import ContractSignatureConsentModal from '../../components/modals/ContractSignatureConsentModal';
import PhoneConfirmationModal from '../../components/modals/PhoneConfirmationModal';
import {
  CONTRACT_SIGNATURE_MODE_CHANGED_EVENT,
  OPEN_CONTRACT_SIGNATURE_LAUNCH_EVENT,
  OpenContractSignatureLaunchDetail,
} from '../../constants/contractSignature';
import { ObjectOf } from '../../types';

const AUTOMATIC = 'automatic';
const MANUAL = 'manual';
const LOCKED_STYLE = { border: 0, margin: 0, padding: 0, opacity: 0.6 };
// Right above the launch button ("Contrat (PDF)" field): without the space
// react-admin keeps for a helper text, nor the input's bottom margin
const COMPACT_PROPS = { helperText: false, margin: 'none' };

/**
 * "Type de signature" (Manuelle / Automatique) of the purchase order's
 * "Contrat" tab, on its contract (`record.contract`), shown to the contract
 * signatory admin only. With their consent in force, the choice is saved with
 * the order ("Enregistrer"). Without, "Automatique" first opens the consent
 * modal: once consented, "Automatique" is saved at once and the launch recap
 * ("Contrat (PDF)" field) opens; closed or canceled, the choice goes back to
 * "Manuelle". Back to "Manuelle" once launched in "Automatique", the
 * signature is launched again at once with the code sent to the admin's
 * phone (the API's ContractSignatureModeService), once their number is
 * checked; canceled, the choice goes back to "Automatique".
 *
 * @param Component
 */
function withContractSignatureMode(
  Component: ComponentType<any>
): FunctionComponent {
  const Extension: FunctionComponent<ObjectOf<any>> = (props) => {
    const { record, source } = props;
    const form = useForm();
    const t = useTranslate();
    const notify = useNotify();
    const refresh = useRefresh();
    const config = useConfig();
    const contract = record?.contract;
    const [consentGiven, setConsentGiven] = useState(
      Boolean(contract?.contract_signatory_consent_given)
    );
    const [modalOpen, setModalOpen] = useState(false);
    const [admin, setAdmin] = useState<{
      first_name?: string;
      last_name?: string;
    }>({});
    const [loading, setLoading] = useState(false);
    const [phoneModalOpen, setPhoneModalOpen] = useState(false);
    const [adminPhone, setAdminPhone] = useState<string>();

    useEffect(() => {
      setConsentGiven(Boolean(contract?.contract_signatory_consent_given));
    }, [contract?.contract_signatory_consent_given]);

    const isSignatory =
      Boolean(contract?.contract_signatory_id) &&
      String(contract?.contract_signatory_id) === String(getCurrentUserId());

    if (!contract || !isSignatory || contract.fully_signed) return null;

    const notifyError = (error: unknown) => {
      const key = `flash.error.${get(error, 'body.message')}`;
      notify(t(key) !== key ? key : 'notify.error.update', 'error');
    };

    // To the "Contrat (PDF)" field, ahead of the purchase order's saving
    const notifyModeChanged = (signatureMode: string) =>
      window.dispatchEvent(
        new CustomEvent<OpenContractSignatureLaunchDetail>(
          CONTRACT_SIGNATURE_MODE_CHANGED_EVENT,
          { detail: { contractId: contract.id, signatureMode } }
        )
      );

    const backToManual = () => {
      setModalOpen(false);
      form.change(source, MANUAL);
      notifyModeChanged(MANUAL);
    };

    const backToAutomatic = () => {
      setPhoneModalOpen(false);
      form.change(source, AUTOMATIC);
      notifyModeChanged(AUTOMATIC);
    };

    const openPhoneModal = async () => {
      setPhoneModalOpen(true);
      try {
        const { json } = await httpClient(
          `${config.apiUrl}/admins/${getCurrentUserId()}`
        );
        setAdminPhone((json as { phone?: string }).phone || '');
      } catch {
        // The admin types their number in.
      }
    };

    const updateAdminPhone = async (phone: string) => {
      await httpClient(`${config.apiUrl}/admins/${getCurrentUserId()}`, {
        method: 'PUT',
        body: JSON.stringify({ phone }),
      });
      setAdminPhone(phone);
    };

    // The signature launched again in "Manuelle" at once, as the modal says
    const confirmPhone = async (phone: string) => {
      setLoading(true);
      try {
        if (phone !== adminPhone) await updateAdminPhone(phone);
        await httpClient(
          `${config.apiUrl}/contracts/${contract.id}/signature_mode`,
          {
            method: 'PATCH',
            body: JSON.stringify({ signature_mode: MANUAL }),
          }
        );
        setPhoneModalOpen(false);
        refresh();
      } catch (error) {
        notifyError(error);
      } finally {
        setLoading(false);
      }
    };

    const handleChange = async (eventOrValue: unknown) => {
      const value =
        typeof eventOrValue === 'string'
          ? eventOrValue
          : get(eventOrValue, 'target.value');
      notifyModeChanged(value);
      if (
        value === MANUAL &&
        contract.signature_launched &&
        contract.signature_mode === AUTOMATIC
      ) {
        openPhoneModal();

        return;
      }
      if (value !== AUTOMATIC || consentGiven) return;

      setModalOpen(true);
      try {
        const { json } = await httpClient(
          `${config.apiUrl}/admins/${getCurrentUserId()}`
        );
        setAdmin(json as { first_name?: string; last_name?: string });
      } catch {
        // The consent text is shown without the admin's name.
      }
    };

    const consent = async () => {
      setLoading(true);

      try {
        await httpClient(`${config.apiUrl}/contract_signature_consent`, {
          method: 'POST',
        });
        setConsentGiven(true);
        await httpClient(
          `${config.apiUrl}/contracts/${contract.id}/signature_mode`,
          {
            method: 'PATCH',
            body: JSON.stringify({ signature_mode: AUTOMATIC }),
          }
        );
        setModalOpen(false);
        window.dispatchEvent(
          new CustomEvent<OpenContractSignatureLaunchDetail>(
            OPEN_CONTRACT_SIGNATURE_LAUNCH_EVENT,
            { detail: { contractId: contract.id, signatureMode: AUTOMATIC } }
          )
        );
      } catch (error) {
        notifyError(error);
        backToManual();
      } finally {
        setLoading(false);
      }
    };

    // The admin's Yousign signer was created for the mode chosen at launch:
    // locked once the expert signed (the API refuses it too). React-admin's
    // radio group has no `disabled`: a disabled fieldset disables its radios.
    if (contract.expert_signed_awaiting_admin) {
      return (
        <fieldset disabled style={LOCKED_STYLE}>
          <Component {...props} {...COMPACT_PROPS} />
        </fieldset>
      );
    }

    return (
      <>
        <Component {...props} {...COMPACT_PROPS} onChange={handleChange} />
        <ContractSignatureConsentModal
          open={modalOpen}
          firstName={admin.first_name}
          lastName={admin.last_name}
          loading={loading}
          handleClose={backToManual}
          handleConsent={consent}
        />
        <PhoneConfirmationModal
          open={phoneModalOpen}
          title="resource.contracts.signature_mode.phone_modal.title"
          description="resource.contracts.signature_mode.phone_modal.description"
          confirmLabel="resource.contracts.signature_mode.phone_modal.confirm"
          initialPhone={adminPhone}
          loading={loading}
          handleClose={backToAutomatic}
          handleUpdatePhone={updateAdminPhone}
          handleConfirm={confirmPhone}
        />
      </>
    );
  };
  Extension.displayName = `withExtension(ContractSignatureMode)`;

  return Extension;
}

export default withContractSignatureMode;
